import React, { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { RuntimePolicySurface } from '../../generated/api/types.gen.js';
import { runtimePoliciesQuery } from '../../services/api/runtime-policy.queries.js';
import { Button } from './Button.js';
import { RuntimePolicyPicker } from './RuntimePolicyPicker.js';

interface RuntimePolicyAssignerProps {
  surface: RuntimePolicySurface;
  /** Subject of the sentence, e.g. "This workflow" ("<label> runs under …"). */
  label: string;
  /** Replaces the default "<label> runs under" lead-in. */
  lead?: string;
  /** The policy's name when the caller already knows it; saves waiting for the policy list. */
  currentName?: string;
  /** The kind of work, shown beside the name (e.g. "workflow"). */
  kindLabel?: string;
  currentPolicyId: number;
  /** When false the current policy is shown read-only. */
  canChange: boolean;
  /** Persists the new policy; the caller invalidates whatever it owns. */
  onAssign: (policyId: number) => Promise<unknown>;
}

/** Shows the policy an entity runs under and lets an authorised person change it. */
export const RuntimePolicyAssigner: React.FC<RuntimePolicyAssignerProps> = ({
  surface,
  label,
  lead,
  currentName,
  kindLabel,
  currentPolicyId,
  canChange,
  onAssign,
}) => {
  const policies = useQuery(runtimePoliciesQuery());
  // A list that could not be read names the policy by its id, never as no policy at all.
  const knownName = policies.isError
    ? `Policy #${currentPolicyId}`
    : (policies.data?.find((policy) => policy.id === currentPolicyId)?.name ?? null);
  const name = currentName ?? knownName;
  const [editing, setEditing] = useState(false);
  const [picked, setPicked] = useState<number>(currentPolicyId);
  const mutation = useMutation({
    mutationFn: (policyId: number) => onAssign(policyId),
    onSuccess: () => setEditing(false),
  });

  return (
    <div className="flex flex-col gap-2" data-testid="runtime-policy-assigner">
      <div className="flex items-center gap-2 text-sm">
        <span className="text-muted">{lead ?? `${label} runs under`}</span>
        <span className="text-heading font-medium">{name ?? `Policy #${currentPolicyId}`}</span>
        {kindLabel && <span className="text-xs text-faint">· {kindLabel}</span>}
        {canChange && !editing && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setPicked(currentPolicyId);
              setEditing(true);
            }}
          >
            Change policy
          </Button>
        )}
      </div>
      {editing && (
        <div className="flex flex-col gap-2">
          <RuntimePolicyPicker surface={surface} value={picked} onChange={setPicked} disabled={mutation.isPending} />
          {mutation.isError && <p className="text-xs text-danger">{mutation.error.message}</p>}
          <div className="flex gap-2">
            <Button
              size="sm"
              loading={mutation.isPending}
              disabled={picked === currentPolicyId}
              onClick={() => mutation.mutate(picked)}
            >
              Save
            </Button>
            <Button variant="secondary" size="sm" onClick={() => setEditing(false)} disabled={mutation.isPending}>
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
